import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../Icon';
import { ICONS } from '../icon-map';
import './dialog.css';

/*
 * Dialog: the only modal container in the product.
 * - role="dialog" (or "alertdialog"), aria-modal, labelled by its title
 * - focus moves in on open, is trapped inside, and returns to the opener on close
 * - Escape closes the top-most dialog only; overlay click closes unless disabled
 * - body scroll is locked while any dialog is open
 *
 * Props: open, onClose, title, description, children, footer,
 *        size: 'sm' | 'md' | 'lg' | 'xl' | 'full', placement: 'center' | 'right' (drawer),
 *        initialFocusRef, closeOnOverlay = true, closeOnEscape = true, hideClose,
 *        role = 'dialog', className, bodyClassName, headerActions
 */

const openStack = [];
let lockCount = 0;

const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
].join(',');

function lockScroll() {
    lockCount += 1;
    if (lockCount === 1) {
        const scrollbar = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
    }
}

function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
    }
}

export default function Dialog({
    open,
    onClose,
    title,
    description,
    children,
    footer,
    size = 'md',
    placement = 'center',
    initialFocusRef,
    closeOnOverlay = true,
    closeOnEscape = true,
    hideClose = false,
    role = 'dialog',
    className = '',
    bodyClassName = '',
    headerActions,
}) {
    const panelRef = useRef(null);
    const openerRef = useRef(null);
    const tokenRef = useRef({});
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const uid = useId();
    const titleId = `dlg-${uid}-title`;
    const descId = `dlg-${uid}-desc`;

    useEffect(() => {
        if (!open) return undefined;
        const token = tokenRef.current;
        openerRef.current = document.activeElement;
        openStack.push(token);
        lockScroll();

        const t = setTimeout(() => {
            const panel = panelRef.current;
            if (!panel) return;
            const target = (initialFocusRef && initialFocusRef.current)
                || panel.querySelector('[data-autofocus]')
                || panel.querySelector(FOCUSABLE)
                || panel;
            target.focus({ preventScroll: true });
        }, 0);

        const onKeyDown = (e) => {
            if (openStack[openStack.length - 1] !== token) return;
            if (e.key === 'Escape' && closeOnEscape) {
                e.stopPropagation();
                if (onCloseRef.current) onCloseRef.current();
                return;
            }
            if (e.key === 'Tab') {
                const panel = panelRef.current;
                if (!panel) return;
                const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
                if (items.length === 0) {
                    e.preventDefault();
                    panel.focus();
                    return;
                }
                const first = items[0];
                const last = items[items.length - 1];
                if (e.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', onKeyDown, true);

        return () => {
            clearTimeout(t);
            document.removeEventListener('keydown', onKeyDown, true);
            const idx = openStack.lastIndexOf(token);
            if (idx !== -1) openStack.splice(idx, 1);
            unlockScroll();
            const opener = openerRef.current;
            if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
                opener.focus({ preventScroll: true });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) return null;

    const onOverlayMouseDown = (e) => {
        if (e.target === e.currentTarget && closeOnOverlay && onClose) onClose();
    };

    return createPortal(
        <div className={`ds-dialog-root ds-dialog-root--${placement}`} onMouseDown={onOverlayMouseDown}>
            <div
                ref={panelRef}
                role={role}
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                aria-describedby={description ? descId : undefined}
                tabIndex={-1}
                className={`ds-dialog ds-dialog--${size} ds-dialog--${placement} ${className}`}
            >
                {(title || !hideClose) && (
                    <div className="ds-dialog__header">
                        <div className="ds-dialog__heading">
                            {title && <h2 id={titleId} className="ds-dialog__title">{title}</h2>}
                            {description && <p id={descId} className="ds-dialog__description">{description}</p>}
                        </div>
                        {headerActions}
                        {!hideClose && onClose && (
                            <button type="button" className="ds-dialog__close" onClick={onClose} aria-label="Close">
                                <Icon glyph={ICONS.close} size="sm" />
                            </button>
                        )}
                    </div>
                )}
                <div className={`ds-dialog__body ${bodyClassName}`}>{children}</div>
                {footer && <div className="ds-dialog__footer">{footer}</div>}
            </div>
        </div>,
        document.body
    );
}

/** Drawer: a Dialog that slides in from the side. */
export function Drawer(props) {
    return <Dialog size="md" {...props} placement="right" />;
}
